"use client";

import { useEffect, useRef, useState } from "react";
import { fetchGlbBlobUrl } from "../lib/api";

// <model-viewer> is a custom element; declare it for TS so JSX accepts the props.
// React 19 + @types/react ≥19 lifts JSX into React.JSX, so we augment it via
// the React module rather than the legacy global JSX namespace.
declare module "react" {
  namespace JSX {
    interface IntrinsicElements {
      "model-viewer": React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLElement> & {
          src?: string;
          alt?: string;
          ar?: boolean;
          "auto-rotate"?: boolean;
          "camera-controls"?: boolean;
          "shadow-intensity"?: string;
          exposure?: string;
          "environment-image"?: string;
        },
        HTMLElement
      >;
    }
  }
}

export interface ModelViewerProps {
  jobId: string;
}

/**
 * Authenticated GLB renderer. <model-viewer> can't include credentials when
 * fetching via its `src` attribute, so we pull the GLB ourselves with cookies
 * and feed it a blob: URL. Carry-forward from TDD-0015 Phase 3.
 */
export function ModelViewer({ jobId }: ModelViewerProps) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const lastBlobRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setBlobUrl(null);
    setError(null);

    // Lazy-load the model-viewer custom element.
    void import("@google/model-viewer").catch((e) => {
      console.warn("[model-viewer] import failed", e);
    });

    fetchGlbBlobUrl(jobId)
      .then((url) => {
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        if (lastBlobRef.current) URL.revokeObjectURL(lastBlobRef.current);
        lastBlobRef.current = url;
        setBlobUrl(url);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError((e as Error).message);
      });

    return () => {
      cancelled = true;
    };
  }, [jobId]);

  useEffect(() => {
    return () => {
      if (lastBlobRef.current) URL.revokeObjectURL(lastBlobRef.current);
      lastBlobRef.current = null;
    };
  }, []);

  if (error) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-red-500">
        {error}
      </div>
    );
  }
  if (!blobUrl) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
        Loading model…
      </div>
    );
  }

  return (
    <model-viewer
      src={blobUrl}
      alt="Generated 3D model"
      camera-controls
      auto-rotate
      shadow-intensity="1"
      style={{ width: "100%", height: "100%" }}
    />
  );
}
